import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import { toast } from "sonner";
import type { Staff } from "../types/staff-management.types";
import { updateAdminUserPassword } from "@/lib/users.api";
import { validatePassword } from "@/core/auth/utils/password.utils";
import styles from "./ChangeStaffPasswordModal.module.scss";

function splitDisplayName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

interface ChangeStaffPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: Staff | null;
  onSuccess?: () => void;
}

const ChangeStaffPasswordModal: React.FC<ChangeStaffPasswordModalProps> = ({
  isOpen,
  onClose,
  staff,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { firstName, lastName } = useMemo(
    () => splitDisplayName(staff?.name ?? ""),
    [staff?.name],
  );

  useEffect(() => {
    if (isOpen && staff) {
      setNewPassword("");
    }
  }, [isOpen, staff?.id, staff]);

  const passwordOk = validatePassword(newPassword).isValid;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!staff || submitting) return;

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.error(
        t(validation.error ?? "login.passwordTooShort", t("staffManagement.editModal.passwordTooShort")),
      );
      return;
    }

    setSubmitting(true);
    try {
      await updateAdminUserPassword(staff.id, { password: newPassword });
      onSuccess?.();
      onClose();
    } catch {
      // apiClient interceptor
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("staffManagement.changePasswordModal.title")}
      size="md"
      withForm
      formId="changeStaffPasswordForm"
      handleSubmit={handleSubmit}
      loading={submitting}
      isFormValid={passwordOk}
      submitButtonText={t("staffManagement.changePasswordModal.confirm")}
    >
      <div className={styles.formStack}>
        <div className={styles.formField}>
          <Input
            label={t("staffManagement.changePasswordModal.firstName")}
            value={firstName}
            readOnly
          />
        </div>
        <div className={styles.formField}>
          <Input
            label={t("staffManagement.changePasswordModal.lastName")}
            value={lastName}
            readOnly
          />
        </div>
        <div className={styles.formField}>
          <Input
            label={t("staffManagement.changePasswordModal.newPassword")}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            placeholder={t("staffManagement.changePasswordModal.newPasswordPlaceholder")}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ChangeStaffPasswordModal;
